import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  BoxProps,
  Button,
  ButtonProps,
  Collapse,
  FormHelperText,
  FormHelperTextProps,
  Grid,
  GridProps,
  IconButton,
  MenuItem,
  Select,
  SelectProps,
  Stack,
  Typography,
  TypographyProps,
  useMediaQuery,
  useTheme,
} from '@mui/material';

import CancelIcon from '@mui/icons-material/Cancel';

import { useAppSelector } from '../../../hooks';
import { TP_COLORS } from '../../../constants';
import { getUrlConvertedString } from '../../../helper';

import {
  ISearchOption,
  ISubTopicForSearch,
  ISubjectForSearch,
  ITopicForSearch,
  getCurriculumState,
} from '../../../store/CurriculumSlice';

const AIStarIcon = `${process.env.PUBLIC_URL}/images/landingPage/ai-star-icon.png`;
const backgroundGradient =
  'linear-gradient(90deg, rgba(219, 134, 248, 0.15) 0%, rgba(0, 111, 214, 0.15) 100%)';

const mainBoxStyles: BoxProps['sx'] = {
  display: 'flex',
  borderRadius: '20px',
  border: '2px solid var(--AI-Gradient, #DB86F8)',
  background: backgroundGradient,
};

const typoGradientStyles = {
  background: 'linear-gradient(90deg, #DB86F8 0%, #006FD6 100%)',
  backgroundClip: 'text',
  WebkitBackgroundClip: 'text',
  WebkitTextFillColor: 'transparent',
};

const introducingTypoProps: TypographyProps = {
  fontSize: '18px',
  fontWeight: '700',
  sx: typoGradientStyles,
};

const titleTypoProps: TypographyProps = {
  variant: 'h4',
  fontWeight: '700',
  sx: {
    ...typoGradientStyles,
    fontSize: { xs: '22px', sm: '24px', md: '26px', lg: '30px' },
  },
};

const descriptionTypoProps: TypographyProps = {
  marginBottom: '15px',
  marginLeft: '40px',
  lineHeight: '1.2',
  sx: {
    ...typoGradientStyles,
    fontSize: { xs: '16px', md: '17px' },
  },
};

const tryButtonProps: ButtonProps = {
  variant: 'outlined',
  color: 'inherit',
  sx: {
    marginLeft: '40px',
    padding: '10px 24px',
    fontWeight: '600',
    color: '#0F172A',
    borderRadius: '23px',
    border: '1px solid var(--AI-Gradient, #DB86F8)',
    background: backgroundGradient,
  },
};

const formBoxStyles: BoxProps['sx'] = {
  display: 'flex',
  alignItems: 'center',
  borderRadius: '10px',
  backgroundSize: '30px',
  marginTop: '20px',
  background: `url(${AIStarIcon}) 45px 13px / 28px 26px no-repeat, url(${AIStarIcon}) 74% 13px / 28px 26px no-repeat, url(${AIStarIcon}) 15% 77% / 28px 26px no-repeat, url(${AIStarIcon}) 99% 90% / 28px 26px no-repeat, ${TP_COLORS.gray.darken}`,
};

const selectProps: SelectProps = {
  fullWidth: true,
  displayEmpty: true,
  size: 'medium',
  sx: {
    color: '#006C9C',
    fontSize: 'inherit',
    height: '50px',
    backgroundColor: TP_COLORS.white,
    borderRadius: '5px',
  },
};

const gridStyles: GridProps['sx'] = {
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'flex-start',
};

const formHelperProps: FormHelperTextProps = {
  error: true,
  sx: { marginLeft: '8px' },
};

const startButtonProps: ButtonProps = {
  fullWidth: true,
  variant: 'outlined',
  color: 'inherit',
  sx: {
    padding: '10px 24px',
    fontWeight: '600',
    color: TP_COLORS.white,
    borderRadius: '23px',
    border: '1px solid var(--AI-Gradient, #DB86F8)',
    background: backgroundGradient,
  },
};

interface ISelectedValues {
  curriculum: ISearchOption | undefined;
  subject?: ISubjectForSearch | undefined;
  topic?: ITopicForSearch | undefined;
  subTopic?: ISubTopicForSearch | undefined;
}

const initialState: ISelectedValues = {
  curriculum: undefined,
  subject: undefined,
  topic: undefined,
  subTopic: undefined,
};

const defaultErrorData = {
  visible: false,
  message: ' ',
};

const initialErrors = {
  curriculum: { ...defaultErrorData },
  subject: { ...defaultErrorData },
  topic: { ...defaultErrorData },
  subTopic: { ...defaultErrorData },
};

const AIMarkingSection: React.FC = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  const belowMd = useMediaQuery(theme.breakpoints.down('md'));
  const belowSm = useMediaQuery(theme.breakpoints.down('sm'));

  // Store
  const curriculumState = useAppSelector(getCurriculumState);
  const { searchOptions } = curriculumState;

  // State
  const [isFormEnabled, setIsFormEnabled] = useState<boolean>(false);
  const [errors, setErrors] = useState(initialErrors);
  const [selectedValues, setSelectedValues] = useState<ISelectedValues>(
    initialState
  );

  const handleCurriculumChange = (option: ISearchOption) => {
    setSelectedValues({ curriculum: option });
    if (errors.curriculum.visible) {
      setErrors((prevState) => ({
        ...prevState,
        curriculum: defaultErrorData,
      }));
    }
  };

  const handleSubjectChange = (option: ISubjectForSearch) => {
    setSelectedValues((prevState) => ({ ...prevState, subject: option }));
    if (errors.subject.visible) {
      setErrors((prevState) => ({
        ...prevState,
        subject: defaultErrorData,
      }));
    }
  };

  const handleTopicChange = (option: ITopicForSearch) => {
    setSelectedValues((prevState) => ({ ...prevState, topic: option }));
    if (errors.topic.visible) {
      setErrors((prevState) => ({
        ...prevState,
        topic: defaultErrorData,
      }));
    }
  };

  const handleSubTopicChange = (option: ISubTopicForSearch) => {
    setSelectedValues((prevState) => ({ ...prevState, subTopic: option }));
    if (errors.subTopic.visible) {
      setErrors((prevState) => ({
        ...prevState,
        subTopic: defaultErrorData,
      }));
    }
  };

  const handleStartQuiz = () => {
    if (!selectedValues.curriculum) {
      setErrors((prevState) => ({
        ...prevState,
        curriculum: {
          visible: true,
          message: 'Select a Curriculum',
        },
      }));
    }
    if (!selectedValues.subject) {
      setErrors((prevState) => ({
        ...prevState,
        subject: {
          visible: true,
          message: 'Select a Subject',
        },
      }));
    }
    if (!selectedValues.topic) {
      setErrors((prevState) => ({
        ...prevState,
        topic: {
          visible: true,
          message: 'Select a Topic',
        },
      }));
    }
    if (!selectedValues.subTopic) {
      setErrors((prevState) => ({
        ...prevState,
        subTopic: {
          visible: true,
          message: 'Select a SubTopic',
        },
      }));
    }

    if (
      selectedValues.curriculum &&
      selectedValues.subject &&
      selectedValues.topic &&
      selectedValues.subTopic
    ) {
      const urlConvertedCurriculum = getUrlConvertedString(
        selectedValues.curriculum.curriculum
      );

      const urlConvertedSubject = getUrlConvertedString(
        selectedValues.subject.subject
      );

      const urlConvertedTopic = getUrlConvertedString(
        selectedValues.topic.name
      );

      const urlConvertedSubTopic = getUrlConvertedString(
        selectedValues.subTopic.name
      );

      navigate(
        `${process.env.PUBLIC_URL}/dashboard/${urlConvertedCurriculum}/${urlConvertedSubject}/${urlConvertedTopic}/${urlConvertedSubTopic}/${selectedValues.subTopic._id}/quiz`
      );
    }
  };

  return (
    <>
      <Box sx={mainBoxStyles}>
        {!belowMd && (
          <Stack
            sx={{
              width: '50%',
              background: `url('${process.env.PUBLIC_URL}/images/landingPage/ai-screenshot-2.png') 113% 100%/83% no-repeat, url('${process.env.PUBLIC_URL}/images/landingPage/ai-screenshot-1.png') 30px 100%/84% no-repeat`,
            }}
          />
        )}

        <Stack
          sx={{
            width: { xs: '100%', md: '50%' },
            padding: '20px',
            paddingLeft: belowMd ? '20px' : '0px',
          }}
        >
          <Stack alignItems="flex-end" marginBottom="10px">
            <Box
              sx={{
                padding: '6px 20px',
                borderRadius: '10px',
                background: TP_COLORS.gray.darken,
              }}
            >
              <Typography {...introducingTypoProps}>Introducing</Typography>
            </Box>
          </Stack>
          {belowMd && (
            <Stack
              sx={{
                height: { xs: '150px', sm: '290px' },
                marginBottom: '30px',
                background: `url('${process.env.PUBLIC_URL}/images/landingPage/ai-screenshot-2.png') 110% 100%/81% no-repeat, url('${process.env.PUBLIC_URL}/images/landingPage/ai-screenshot-1.png') 20px 100%/80% no-repeat`,
              }}
            />
          )}

          <Stack
            alignItems="flex-start"
            justifyContent="center"
            minHeight={!belowMd ? '170px' : undefined}
          >
            <Box display="flex" alignItems="center" marginBottom="10px">
              <img
                src={AIStarIcon}
                alt="ai-logo"
                style={{ width: '28px', height: '26px', marginRight: '10px' }}
              />
              <Typography {...titleTypoProps}>
                Detailed Marking Scheme with AI
              </Typography>
            </Box>

            <Typography {...descriptionTypoProps}>
              Now you can check a detailed marking scheme with AI assistance
              that provides step-by-step grades, feedback, and personalized
              suggestions to help you improve and succeed
            </Typography>
            <Collapse in={!isFormEnabled}>
              <Button
                {...tryButtonProps}
                onClick={() => setIsFormEnabled(true)}
              >
                Try It Now
              </Button>
            </Collapse>
          </Stack>
        </Stack>
      </Box>

      <Collapse in={isFormEnabled}>
        <Box
          sx={{
            ...formBoxStyles,
            padding: `30px ${belowMd ? '30px' : '0px'} ${
              belowSm ? '30px' : '10px'
            } 30px`,
          }}
        >
          <Grid container spacing={2}>
            {/* Curriculum */}
            <Grid item xs={12} sm={4} md={2.25}>
              <Select
                {...selectProps}
                value={selectedValues.curriculum?.curriculum || ''}
                error={errors.curriculum.visible}
                renderValue={
                  selectedValues.curriculum === undefined
                    ? () => 'Curriculum*'
                    : undefined
                }
              >
                {searchOptions.map((item, index) => (
                  <MenuItem
                    key={index}
                    value={item.curriculum}
                    onClick={() => handleCurriculumChange(item)}
                  >
                    {item.curriculum}
                  </MenuItem>
                ))}
              </Select>
              <FormHelperText {...formHelperProps}>
                {errors.curriculum.message}
              </FormHelperText>
            </Grid>
            {/* Subject */}
            <Grid item xs={12} sm={4} md={2.25}>
              <Select
                {...selectProps}
                value={selectedValues.subject?.subjectCode || ''}
                disabled={!selectedValues.curriculum}
                error={errors.subject.visible}
                renderValue={
                  selectedValues.subject === undefined
                    ? () => 'Subject*'
                    : undefined
                }
              >
                {selectedValues.curriculum &&
                selectedValues.curriculum.subjects.length
                  ? selectedValues.curriculum.subjects
                      .filter((item) => item.topics.length)
                      .map((item, index) => (
                        <MenuItem
                          key={index}
                          value={item.subjectCode}
                          onClick={() => handleSubjectChange(item)}
                        >
                          {item.subject}
                        </MenuItem>
                      ))
                  : null}
              </Select>
              <FormHelperText {...formHelperProps}>
                {errors.subject.message}
              </FormHelperText>
            </Grid>
            {/* Topic */}
            <Grid item xs={12} sm={4} md={2.25}>
              <Select
                {...selectProps}
                value={selectedValues.topic?._id || ''}
                disabled={!selectedValues.subject}
                error={errors.topic.visible}
                renderValue={
                  selectedValues.topic === undefined
                    ? () => 'Topic*'
                    : undefined
                }
              >
                {selectedValues.subject && selectedValues.subject.topics.length
                  ? selectedValues.subject.topics
                      .filter((item) => item.name !== 'Overview')
                      .map((item, index) => (
                        <MenuItem
                          key={index}
                          value={item._id}
                          onClick={() => handleTopicChange(item)}
                        >
                          {item.name}
                        </MenuItem>
                      ))
                  : null}
              </Select>
              <FormHelperText {...formHelperProps}>
                {errors.topic.message}
              </FormHelperText>
            </Grid>
            {/* SubTopic */}
            <Grid item xs={12} sm={4} md={2.25}>
              <Select
                {...selectProps}
                value={selectedValues.subTopic?._id || ''}
                disabled={!selectedValues.topic}
                error={errors.subTopic.visible}
                renderValue={
                  selectedValues.subTopic === undefined
                    ? () => 'SubTopic*'
                    : undefined
                }
              >
                {selectedValues.topic && selectedValues.topic.subTopics.length
                  ? selectedValues.topic.subTopics
                      .filter(
                        (item) =>
                          item.name !== 'Topic Planner' &&
                          item.name !== 'Topic Overview'
                      )
                      .map((item, index) => (
                        <MenuItem
                          key={index}
                          value={item._id}
                          onClick={() => handleSubTopicChange(item)}
                        >
                          {item.name}
                        </MenuItem>
                      ))
                  : null}
              </Select>
              <FormHelperText {...formHelperProps}>
                {errors.subTopic.message}
              </FormHelperText>
            </Grid>
            <Grid item xs={8} sm={4} md={2} sx={gridStyles}>
              <Button {...startButtonProps} onClick={handleStartQuiz}>
                Start a Quiz
              </Button>
            </Grid>
            <Grid item xs={4} sm={4} md={1} sx={gridStyles}>
              <IconButton
                color="secondary"
                size="small"
                onClick={() => {
                  setIsFormEnabled(false);
                  setErrors(initialErrors);
                }}
              >
                <CancelIcon fontSize="large" />
              </IconButton>
            </Grid>
          </Grid>
        </Box>
      </Collapse>
    </>
  );
};

export default AIMarkingSection;
